/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useContext, useCallback } from "react";

// internal-deps
import {
    RenderingDetails,
    SavingsLineItemData,
    SavingsRightSectionType,
    ReviewedCartApiResponse,
    ApplyCouponsWidgetInfo,
    GiftingApplyCouponWidgetInfo,
} from "@swiggy-private/cart-types";
import { AnalyticsContext, isNonEmptyString } from "@swiggy-private/checkout-core";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// hooks
import { useCartRendererXP } from "./useCartRendererXP";
import { useCouponSavingsActions } from "./useCouponSavingsActions";

// helpers
import {
    getFallbackCouponIcon,
    getPotentialSavingsText,
    isPaymentCouponUsecase,
    showCouponCodesListInGiftingCartSavingsCorner,
    getPotentialSavingsTextsForGiftingCart,
    getPotentialSavingsTextMROCart,
    getMROApplyRestaurantCouponText,
} from "@cart-client/helpers/Coupon";
import { getCartId } from "@cart-client/helpers/Cart";
import { isEmpty } from "@swiggy-private/js-utils";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";

// constants
import { FTUE_FOOTER_COMMS_FOR_POR_2 } from "@cart-client/constants/Coupon";

// analytics
import { impressionDualApplicationCapability } from "@cart-client/analytics/events/SavingsCorner";
import { useGiftingCouponCodeAnalytics } from "./useGiftingCouponCodeAnalytics";
import { SAVINGS_LINE_ITEM_BOTTOM_SHEET_TYPE } from "@swiggy-private/cart-ui";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";

export interface IProps {
    couponSavingsRenderingDetails: RenderingDetails[];
}

export interface UseCouponButtonSavingsReturnType {
    getCouponButtonSavingsItem: (
        renderingItemData: RenderingDetails,
        applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"],
    ) => SavingsLineItemData;
}

export const useCouponButtonSavingsData = (
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"],
    onSavingsLineItemLoad: (
        title?: string,
        subtitle?: string,
        isPaymentCouponWidget?: boolean,
        restaurantId?: string | number,
    ) => void,
): UseCouponButtonSavingsReturnType => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const { impressionEvent } = useContext(AnalyticsContext);
    const { data } = useContext(CartFlowContext);
    const isMROCart = isMROFlow(data?.cartSource);

    const { xpSelectiveCouponVariant } = useCartRendererXP(cartResponse);

    const { onApplyCouponSectionClick } = useCouponSavingsActions({
        applyPaymentCouponWidgetData,
    });

    const { handleImpressionForGiftingCouponCode } = useGiftingCouponCodeAnalytics();

    const onFtueCommsNudgeLoad = useCallback(() => {
        const cartId = !isEmpty(cartResponse) ? getCartId(cartResponse) : "";
        impressionDualApplicationCapability(impressionEvent, cartId);
    }, [cartResponse, impressionEvent]);

    const getCouponButtonSavingsItem = useCallback(
        (
            renderingItemData: RenderingDetails,
            // scoped per rendering detail (MRO: per restaurant), falls back to global upstream
            applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"],
        ): SavingsLineItemData => {
            const isPaymentCouponWidget = isPaymentCouponUsecase(renderingItemData.type);

            // MRO: override BAU display_text with RX-scoped title
            // ("Apply coupon for {RX}" / "Save {X} on {RX}") using mroChildCartRestaurantId
            let titleValue = renderingItemData.display_text;
            if (isMROCart) {
                titleValue = getMROApplyRestaurantCouponText(cartResponse as MROReviewedCart, applyCouponWidgetData);
            }

            const title = {
                value: titleValue,
                showNewTag: isPaymentCouponWidget,
            };

            const getPotentialSavingsTextHelper = isMROCart ? getPotentialSavingsTextMROCart : getPotentialSavingsText;
            let potentialSavingsText = getPotentialSavingsTextHelper(
                cartResponse,
                applyCouponWidgetData,
                xpSelectiveCouponVariant,
            );
            const potentialSavingsGiftingCartText = getPotentialSavingsTextsForGiftingCart(
                applyCouponWidgetData as GiftingApplyCouponWidgetInfo,
            );
            potentialSavingsText = isNonEmptyString(potentialSavingsGiftingCartText)
                ? potentialSavingsGiftingCartText
                : potentialSavingsText;

            const subtitle = isNonEmptyString(potentialSavingsText)
                ? {
                      value: potentialSavingsText,
                  }
                : undefined;

            const rightSection = {
                type: SavingsRightSectionType.ICON,
            };

            let couponCodeList;
            if (showCouponCodesListInGiftingCartSavingsCorner(applyCouponWidgetData)) {
                couponCodeList = {
                    applied: false,
                    codes: (applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes,
                    impressionEventCallback: handleImpressionForGiftingCouponCode,
                };
            }

            return {
                icon: renderingItemData.icon || getFallbackCouponIcon(renderingItemData.type),
                title,
                subtitle,
                rightSection,
                onLineItemClick: () => onApplyCouponSectionClick({ isPaymentCouponWidget, applyCouponWidgetData }),
                onLoad: () => {
                    onSavingsLineItemLoad(
                        title.value,
                        subtitle?.value,
                        isPaymentCouponWidget,
                        applyCouponWidgetData?.mroChildCartRestaurantId,
                    );
                },
                footerType: isPaymentCouponWidget
                    ? SAVINGS_LINE_ITEM_BOTTOM_SHEET_TYPE.TOOLTIP
                    : SAVINGS_LINE_ITEM_BOTTOM_SHEET_TYPE.FOOTER_COMMS,
                footerCommsStrip: isPaymentCouponWidget
                    ? {
                          ...FTUE_FOOTER_COMMS_FOR_POR_2,
                          onLoad: onFtueCommsNudgeLoad,
                      }
                    : applyCouponWidgetData?.potential_coupon_info,
                couponCodeList,
            };
        },
        [
            isMROCart,
            cartResponse,
            onApplyCouponSectionClick,
            onFtueCommsNudgeLoad,
            onSavingsLineItemLoad,
            xpSelectiveCouponVariant,
            handleImpressionForGiftingCouponCode,
        ],
    );

    return { getCouponButtonSavingsItem };
};
